import { Button, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { OEMSettings } from '../../../../hooks/useGetSettings';
import { useGetLocations } from '../../../../hooks/useGetLocations';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import { SettingsFlow, useSettingsDetailCrumbs } from '../../../../components/settings/primitives';
import OemMakeStep from './OemMakeStep';
import OemRebateStep from './OemRebateStep';
import {
    buildOemDraft,
    savedCertificationIds,
    toOemPayload,
    type OemDraft,
    type OemOption,
} from './oemDraft';

export interface OemDetailSectionProps {
    /** A make id, or 'new' for the add case. */
    oemId: string;
    oems: Array<OEMSettings>;
    oemOptions: Array<OemOption>;
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

type StepId = 'make' | 'rebates';

/**
 * Add or edit one OEM, full screen and step by step: the make and where it is
 * certified, then the rebate program if there is one. Replaces the two-step OEM
 * modal, so the same screens serve adding and editing.
 *
 * Saving writes the whole oem array through POST /settings/update, which is how
 * this endpoint works, so the draft is merged into the current list here. Each
 * related make picked on step 1 is saved as its own entry with the same
 * settings, replacing whatever that make had.
 */
const OemDetailSection = ({ oemId, oems, oemOptions, refetch }: OemDetailSectionProps) => {
    const navigate = useNavigate();
    // The rebate cell in the OEM table links straight to the rebates step, so
    // the flow can open on either screen.
    const routerLocation = useLocation();
    const initialStep: StepId =
        (routerLocation.state as { step?: StepId } | null)?.step === 'rebates'
            ? 'rebates'
            : 'make';
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const { data: locationsData } = useGetLocations();

    const isAddMode = oemId === 'new';
    const oem = isAddMode ? undefined : oems.find((candidate) => candidate.makeId === oemId);

    const [stepId, setStepId] = useState<StepId>(initialStep);
    const [draft, setDraft] = useState<OemDraft>(() => buildOemDraft(oem));

    // Real shops only; the synthetic 'all' entry is the table's header row.
    const locations = useMemo(
        () => locationsData?.locations?.filter((location) => location._id !== 'all') ?? [],
        [locationsData],
    );
    const allLocationIds = useMemo(
        () => locations.map((location) => location._id as string),
        [locations],
    );

    // Locations arrive from their own query, so the certified list is seeded once
    // they land rather than on mount, and only once — otherwise a refetch would
    // wipe the user's picks.
    const seededLocationsRef = useRef(false);
    useEffect(() => {
        if (seededLocationsRef.current || allLocationIds.length === 0) return;
        seededLocationsRef.current = true;
        setDraft((prev) => ({
            ...prev,
            locationIds: savedCertificationIds(oem, locations, allLocationIds),
        }));
    }, [allLocationIds, locations, oem]);

    // An unknown make id has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !oem) navigate('/settings/oem', { replace: true });
    }, [isAddMode, oem, navigate]);

    const addedMakeIds = useMemo(
        () => oems.map((entry) => entry.makeId).filter((makeId) => makeId !== oem?.makeId),
        [oems, oem?.makeId],
    );

    useSettingsDetailCrumbs([{ label: isAddMode ? 'Add OEM' : draft.name || 'OEM' }]);

    const closeFlow = () => navigate('/settings/oem');

    const hasMake = draft.makeId.length > 0;

    const handleSelectMake = (option: OemOption) => {
        setDraft((prev) => ({
            ...prev,
            makeId: option._id,
            name: option.name,
            // The OEM's other makes are normally certified together, so they
            // start included; ones already configured stay out of it.
            similarMakeIds: option.similarMakes
                .filter((make) => !oems.some((entry) => entry.makeId === make._id))
                .map((make) => make._id),
        }));
    };

    const handleClearMake = () => {
        setDraft((prev) => ({ ...prev, makeId: '', name: '', similarMakeIds: [] }));
    };

    const handleSave = () => {
        if (!hasMake) return;

        const duplicate = oems.find(
            (entry) => entry.makeId === draft.makeId && entry.makeId !== oem?.makeId,
        );
        if (duplicate) {
            toast({
                title: 'Make already added',
                description: `${duplicate.name} already has an OEM entry.`,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const selectedOption = oemOptions.find((option) => option._id === draft.makeId);
        const primary = toOemPayload(
            draft,
            draft.makeId,
            draft.name,
            locations,
            oems.find((entry) => entry.makeId === draft.makeId),
        );
        const related = (selectedOption?.similarMakes ?? [])
            .filter((make) => draft.similarMakeIds.includes(make._id))
            .map((make) =>
                toOemPayload(
                    draft,
                    make._id,
                    make.name,
                    locations,
                    oems.find((entry) => entry.makeId === make._id),
                ),
            );

        // Every make written here replaces its own entry, so the list is rebuilt
        // without them and they are appended once.
        const written = new Set([primary, ...related].map((entry) => entry.makeId));
        const nextOems = [
            ...oems.filter((entry) => entry.makeId !== oem?.makeId && !written.has(entry.makeId)),
            primary,
            ...related,
        ];

        updateSettings.mutate(
            { oem: nextOems },
            {
                onSuccess: () => {
                    toast({
                        title: oem ? 'OEM updated' : 'OEM added',
                        description:
                            related.length > 0
                                ? `${draft.name} and ${related.length} related make${related.length === 1 ? '' : 's'} saved.`
                                : `${draft.name} has been saved.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: oem ? 'Could not update OEM' : 'Could not add OEM',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleRemove = () => {
        if (!oem) return;
        updateSettings.mutate(
            { oem: oems.filter((entry) => entry.makeId !== oem.makeId) },
            {
                onSuccess: () => {
                    toast({
                        title: 'OEM removed',
                        description: `${oem.name} has been removed.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: 'Could not remove OEM',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    if (!isAddMode && !oem) return null;

    return (
        <SettingsFlow
            steps={[
                { id: 'make', label: 'OEM certification' },
                // Nothing to configure until there is a make to configure.
                { id: 'rebates', label: 'Rebates (optional)', isDisabled: !hasMake },
            ]}
            activeStepId={stepId}
            onStepSelect={(id) => {
                if (id === 'rebates' && !hasMake) return;
                setStepId(id as StepId);
            }}
            onCancel={closeFlow}
            onBack={stepId === 'rebates' ? () => setStepId('make') : undefined}
            onNext={() => setStepId('rebates')}
            isNextDisabled={!hasMake}
            onSave={handleSave}
            saveLabel={isAddMode ? 'Add OEM' : 'Save OEM'}
            isSaveDisabled={!hasMake}
            isSaving={updateSettings.isPending}
            actions={
                oem ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemove}
                        isDisabled={updateSettings.isPending}
                    >
                        Remove OEM
                    </Button>
                ) : undefined
            }
        >
            {stepId === 'make' ? (
                <OemMakeStep
                    draft={draft}
                    onChange={setDraft}
                    isEditMode={!!oem}
                    options={oemOptions}
                    addedMakeIds={addedMakeIds}
                    locations={locations}
                    onSelectMake={handleSelectMake}
                    onClearMake={handleClearMake}
                />
            ) : (
                <OemRebateStep draft={draft} onChange={setDraft} />
            )}
        </SettingsFlow>
    );
};

export default OemDetailSection;
